import { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePhotoPreview } from '@/components/PhotoPreview';

export default function LeaderboardsPage({ user: propUser, onLogout }) {
  const previewPhoto = usePhotoPreview();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(propUser);
  const [activeTab, setActiveTab] = useState('potw');
  const [gamesLeaderboard, setGamesLeaderboard] = useState([]);
  const [ratingLeaderboard, setRatingLeaderboard] = useState([]);
  const [kudosLeaderboard, setKudosLeaderboard] = useState([]);
  const [mvpLeaderboard, setMvpLeaderboard] = useState([]);
  const [playersOfWeek, setPlayersOfWeek] = useState({ game: null, players: [], message: '' });
  const [weeklyKudos, setWeeklyKudos] = useState({ game: null, players: [], message: '' });

  useEffect(() => {
    loadUser();
    loadLeaderboards();
  }, []);

  const loadUser = async () => {
    if (!user) {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        console.error('Failed to load user:', error);
      }
    }
  };

  const loadLeaderboards = async () => {
    try {
      const [games, ratings, kudos, mvp, potw, weeklyKudosData] = await Promise.all([
        api.get('/leaderboards/games-played?limit=50'),
        api.get('/leaderboards/community-rating?limit=50'),
        api.get('/leaderboards/kudos?limit=50'),
        api.get('/leaderboards/mvp-awards?limit=50'),
        api.get('/leaderboards/players-of-week'),
        api.get('/leaderboards/weekly-kudos')
      ]);

      setGamesLeaderboard(games.data);
      setRatingLeaderboard(ratings.data);
      setKudosLeaderboard(kudos.data);
      setMvpLeaderboard(mvp.data);
      setPlayersOfWeek(potw.data);
      setWeeklyKudos(weeklyKudosData.data);
    } catch (error) {
      toast.error('Failed to load leaderboards');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getMedalIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  const getMedalColor = (rank) => {
    if (rank === 1) return 'from-yellow-400 to-yellow-600';
    if (rank === 2) return 'from-gray-300 to-gray-500';
    if (rank === 3) return 'from-orange-400 to-orange-600';
    return 'from-blue-50 to-blue-100';
  };

  const getKudosIcon = (type) => {
    const icons = {
      teamwork: '🤝',
      positive_energy: '😊',
      supportive: '💪',
      great_spike: '🏐',
      great_serve: '💥',
      great_pass: '🎯',
      great_defense: '🛡️',
      strong_block: '🚫',
      great_setting: '🙌',
      // Legacy support for old kudos types
      friendly: '😊',
      performance: '⚡',
      communication: '💬'
    };
    return icons[type] || '⭐';
  };

  const renderLeaderboardEntry = (entry, rank, type) => {
    const isMedal = rank <= 3;
    const medalIcon = getMedalIcon(rank);

    return (
      <div
        key={entry.user_id}
        className={`flex items-center gap-2 sm:gap-4 p-3 sm:p-4 rounded-xl transition-all hover:shadow-lg ${
          isMedal ? `bg-gradient-to-r ${getMedalColor(rank)} shadow-md` : 'bg-white hover:bg-gray-50'
        }`}
      >
        {/* Rank */}
        <div className="text-center min-w-[40px] sm:min-w-[60px]">
          {medalIcon ? (
            <div className="text-3xl sm:text-4xl md:text-5xl">{medalIcon}</div>
          ) : (
            <div className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-400">#{rank}</div>
          )}
        </div>

        {/* Profile Photo */}
        <div className="flex-shrink-0">
          {entry.profile_photo_url ? (
            <img
              src={entry.profile_photo_url}
              alt={entry.name}
              loading="lazy"
              onClick={() => previewPhoto(entry.profile_photo_url, entry.name)}
              className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full object-cover cursor-zoom-in ${
                isMedal ? 'border-4 border-white shadow-lg' : 'border-2 border-gray-300'
              }`}
            />
          ) : (
            <div className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center text-lg sm:text-xl md:text-2xl font-bold ${
              isMedal ? 'bg-white text-gray-700 border-4 border-white shadow-lg' : 'bg-blue-500 text-white'
            }`}>
              {entry.name.charAt(0)}
            </div>
          )}
        </div>

        {/* Player Info */}
        <div className="flex-1 min-w-0">
          <div className={`font-bold text-sm sm:text-base md:text-lg truncate ${
            isMedal ? 'text-gray-900' : 'text-gray-800'
          }`}>
            {entry.name}
          </div>
          <div className={`text-xs sm:text-sm ${
            isMedal ? 'text-gray-700' : 'text-gray-500'
          }`}>
            {type === 'games' && `${entry.games_played} games played`}
            {type === 'rating' && `${entry.rating.toFixed(2)}/10 • ${entry.total_ratings || entry.ratings_count} ratings`}
            {type === 'kudos' && (
              <div className="flex flex-wrap gap-1">
                {Object.entries(entry.kudos_breakdown || {}).map(([kudosType, count]) => (
                  count > 0 && (
                    <span key={kudosType} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white/50 rounded text-xs">
                      {getKudosIcon(kudosType)} {count}
                    </span>
                  )
                ))}
              </div>
            )}
            {type === 'mvp' && `${entry.mvp_count} MVP awards`}
          </div>
        </div>

        {/* Score */}
        <div className={`text-right font-bold text-xl sm:text-2xl md:text-3xl ${
          isMedal ? 'text-gray-900' : 'text-blue-600'
        }`}>
          {type === 'games' && entry.games_played}
          {type === 'rating' && entry.rating.toFixed(2)}
          {type === 'kudos' && entry.total_kudos}
          {type === 'mvp' && entry.mvp_count}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-xl text-gray-600">Loading leaderboards...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 mb-2 sm:mb-4">
            🏆 Leaderboards
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-gray-600">Compete with the best players in the community</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 sm:mb-8 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('potw')}
            className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-semibold whitespace-nowrap transition-all text-xs sm:text-sm md:text-base ${
              activeTab === 'potw'
                ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            ⭐ Players of the Week
          </button>
          <button
            onClick={() => setActiveTab('weeklyKudos')}
            className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-semibold whitespace-nowrap transition-all text-xs sm:text-sm md:text-base ${
              activeTab === 'weeklyKudos'
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            🎖️ Weekly Kudos
          </button>
          <button
            onClick={() => setActiveTab('rating')}
            className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-semibold whitespace-nowrap transition-all text-xs sm:text-sm md:text-base ${
              activeTab === 'rating'
                ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            ⭐ Best Community Rating
          </button>
          <button
            onClick={() => setActiveTab('kudos')}
            className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-semibold whitespace-nowrap transition-all text-xs sm:text-sm md:text-base ${
              activeTab === 'kudos'
                ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            💝 Most Kudos Received
          </button>
          <button
            onClick={() => setActiveTab('mvp')}
            className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-semibold whitespace-nowrap transition-all text-xs sm:text-sm md:text-base ${
              activeTab === 'mvp'
                ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            🏅 Most MVP Awards
          </button>
          <button
            onClick={() => setActiveTab('games')}
            className={`px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-lg font-semibold whitespace-nowrap transition-all text-xs sm:text-sm md:text-base ${
              activeTab === 'games'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-100'
            }`}
          >
            🏐 Most Games Played
          </button>
        </div>

        {/* Leaderboard Content */}
        <Card className="shadow-xl">
          <CardHeader className={`p-4 sm:p-6 bg-gradient-to-r ${
            activeTab === 'games' ? 'from-blue-50 to-blue-100' :
            activeTab === 'rating' ? 'from-green-50 to-green-100' :
            activeTab === 'kudos' ? 'from-purple-50 to-purple-100' :
            activeTab === 'potw' ? 'from-pink-50 to-rose-100' :
            activeTab === 'weeklyKudos' ? 'from-orange-50 to-orange-100' :
            'from-yellow-50 to-yellow-100'
          }`}>
            <CardTitle className="text-xl sm:text-2xl">
              {activeTab === 'games' && '🏐 Most Games Played'}
              {activeTab === 'rating' && '⭐ Best Community Rating'}
              {activeTab === 'kudos' && '💝 Most Kudos Received'}
              {activeTab === 'mvp' && '🏅 Most MVP Awards'}
              {activeTab === 'potw' && '⭐ Players of the Week'}
              {activeTab === 'weeklyKudos' && '🎖️ Weekly Kudos'}
            </CardTitle>
            {/* Game info for Players of the Week */}
            {activeTab === 'potw' && playersOfWeek.game && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-pink-200">
                <div className="text-xs sm:text-sm text-gray-600">
                  <div className="font-semibold text-pink-700 mb-1">From Latest Game:</div>
                  <div><strong>{playersOfWeek.game.title}</strong></div>
                  <div>{playersOfWeek.game.date} • {playersOfWeek.game.location}</div>
                </div>
              </div>
            )}
            {/* Game info for Weekly Kudos */}
            {activeTab === 'weeklyKudos' && weeklyKudos.game && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-orange-200">
                <div className="text-xs sm:text-sm text-gray-600">
                  <div className="font-semibold text-orange-700 mb-1">From Latest Game:</div>
                  <div><strong>{weeklyKudos.game.title}</strong></div>
                  <div>{weeklyKudos.game.date} • {weeklyKudos.game.location}</div>
                </div>
                {/* Show rating window message if still open */}
                {weeklyKudos.rating_window_open && (
                  <div className="mt-3 p-3 bg-yellow-50 border border-yellow-300 rounded-lg">
                    <div className="flex items-start gap-2">
                      <span className="text-lg">⏳</span>
                      <div className="text-xs sm:text-sm">
                        <div className="font-semibold text-yellow-800 mb-1">Results Hidden - Rating in Progress</div>
                        <div className="text-yellow-700">{weeklyKudos.message}</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Kudos Legend */}
            {(activeTab === 'kudos' || activeTab === 'weeklyKudos') && (
              <div className={`mt-4 p-3 sm:p-4 bg-white rounded-lg border ${activeTab === 'kudos' ? 'border-purple-200' : 'border-orange-200'}`}>
                <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Kudos Types:</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 text-xs sm:text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">🤝</span>
                    <span className="text-gray-600"><strong>Teamwork:</strong> Great team player</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">😊</span>
                    <span className="text-gray-600"><strong>Positive Energy:</strong> Great vibes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">💪</span>
                    <span className="text-gray-600"><strong>Supportive:</strong> Encouraging</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">🏐</span>
                    <span className="text-gray-600"><strong>Great Spike:</strong> Powerful attacks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">💥</span>
                    <span className="text-gray-600"><strong>Great Serve:</strong> Strong serves</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">🎯</span>
                    <span className="text-gray-600"><strong>Great Pass:</strong> Accurate passing</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">🛡️</span>
                    <span className="text-gray-600"><strong>Great Defense:</strong> Strong defense</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">🚫</span>
                    <span className="text-gray-600"><strong>Strong Block:</strong> Effective blocks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-base sm:text-lg">🙌</span>
                    <span className="text-gray-600"><strong>Great Setting:</strong> Perfect sets</span>
                  </div>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
            <div className="space-y-3">
              {activeTab === 'games' && gamesLeaderboard.length > 0 && (
                gamesLeaderboard.map((entry, idx) => 
                  renderLeaderboardEntry(entry, idx + 1, 'games')
                )
              )}
              
              {activeTab === 'rating' && ratingLeaderboard.length > 0 && (
                ratingLeaderboard.map((entry, idx) => 
                  renderLeaderboardEntry(entry, idx + 1, 'rating')
                )
              )}
              
              {activeTab === 'kudos' && kudosLeaderboard.length > 0 && (
                kudosLeaderboard.map((entry, idx) => 
                  renderLeaderboardEntry(entry, idx + 1, 'kudos')
                )
              )}
              
              {activeTab === 'mvp' && mvpLeaderboard.length > 0 && (
                mvpLeaderboard.map((entry, idx) => 
                  renderLeaderboardEntry(entry, idx + 1, 'mvp')
                )
              )}
              
              {activeTab === 'potw' && playersOfWeek.players.length > 0 && (
                playersOfWeek.players.map((entry, idx) => 
                  renderLeaderboardEntry(entry, idx + 1, 'rating')
                )
              )}
              
              {activeTab === 'weeklyKudos' && weeklyKudos.players.length > 0 && (
                weeklyKudos.players.map((entry, idx) => 
                  renderLeaderboardEntry(entry, idx + 1, 'kudos')
                )
              )}

              {/* Empty State */}
              {activeTab === 'games' && gamesLeaderboard.length === 0 && (
                <div className="text-center py-12 text-sm sm:text-base text-gray-500">
                  No games played yet. Be the first!
                </div>
              )}
              {activeTab === 'rating' && ratingLeaderboard.length === 0 && (
                <div className="text-center py-12 text-sm sm:text-base text-gray-500">
                  No ratings yet. Play and rate games!
                </div>
              )}
              {activeTab === 'kudos' && kudosLeaderboard.length === 0 && (
                <div className="text-center py-12 text-sm sm:text-base text-gray-500">
                  No kudos given yet. Start recognizing great players!
                </div>
              )}
              {activeTab === 'mvp' && mvpLeaderboard.length === 0 && (
                <div className="text-center py-12 text-sm sm:text-base text-gray-500">
                  No MVP awards yet. Play games and earn ratings!
                </div>
              )}
              {activeTab === 'potw' && playersOfWeek.players.length === 0 && (
                <div className="text-center py-12 text-sm sm:text-base text-gray-500">
                  {playersOfWeek.message || 'No rated players from recent games yet!'}
                </div>
              )}
              {activeTab === 'weeklyKudos' && weeklyKudos.players.length === 0 && (
                <div className="text-center py-12 text-sm sm:text-base text-gray-500">
                  {weeklyKudos.message || 'No kudos from recent games yet!'}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
